import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useNotifications } from '@/hooks/use-notifications';
import { useRelativeTime } from '@/hooks/use-relative-time';
import type { Notification } from '@ezrecipebox/contracts';
import { Bell, Check, CheckCheck, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

function NotificationCard({ notification, onMarkAsRead, onDelete }: {
  notification: Notification;
  onMarkAsRead: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const relativeTime = useRelativeTime(notification.createdAt as unknown as Date);

  const handleClick = async () => {
    // Mark as read if unread
    if (!notification.isRead) {
      await onMarkAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.recipeId) {
      navigate(`/recipe/${notification.recipeId}`);
    } else if (notification.recipeBoxId) {
      navigate(`/recipe-box/${notification.recipeBoxId}`);
    } else if (notification.invitationToken) {
      navigate(`/invite?token=${notification.invitationToken}`);
    }
  };

  const handleMarkAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMarking(true);
    await onMarkAsRead(notification.id);
    setIsMarking(false);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleting(true);
    await onDelete(notification.id);
  };

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:border-primary/50',
        !notification.isRead && 'border-l-4 border-l-primary bg-muted/30',
        isDeleting && 'opacity-50 pointer-events-none'
      )}
      onClick={handleClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{notification.title}</CardTitle>
              {!notification.isRead && (
                <span className="flex h-2 w-2 rounded-full bg-primary" />
              )}
            </div>
            <CardDescription className="text-xs">{relativeTime}</CardDescription>
          </div>
          <div className="flex items-center gap-1">
            {!notification.isRead && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleMarkAsRead}
                disabled={isMarking}
                title="Mark as read"
              >
                <Check className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-muted-foreground">{notification.body}</p>
      </CardContent>
    </Card>
  );
}

export default function NotificationsPage() {
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const handleMarkAllAsRead = async () => {
    setIsMarkingAll(true);
    await markAllAsRead();
    setIsMarkingAll(false);
  };

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id);
  };

  const handleDelete = async (id: string) => {
    await deleteNotification(id);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notifications</h1>
          <p className="text-muted-foreground">
            {unreadCount > 0 ? `You have ${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            onClick={handleMarkAllAsRead}
            disabled={isMarkingAll}
            variant="outline"
          >
            <CheckCheck className="mr-2 h-4 w-4" />
            Mark all as read
          </Button>
        )}
      </div>

      <Separator />

      {/* Notifications List */}
      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
            <p className="text-sm text-muted-foreground">Loading notifications...</p>
          </div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center">
          <Bell className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold mb-2">No notifications yet</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            When you receive notifications about recipes, invitations, or updates, they'll appear here.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
